import React, {useMemo} from 'react';
import {
  Dialog,
  DialogContent,
  Heading,
  Button,
  DialogFooter,
  MatchesMediaQuery,
  CheckSmallIcon,
  Icon,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import {useTaopix} from '../../../Context/TaopixContext';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {AIMode, CurrencyAdapter} from '@taopix/taopix-core';
import {useSelector} from 'react-redux';
import {TotalPageCountSelector} from '../../../../Selectors';
import useSettings from '../../../../Hooks/useSettings';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';

export interface StartSmartDesignDialogProps {
  open: boolean;
  onStartSmartDesign: () => void;
  onAutoFill: () => void;
  onClose: () => void;
}

const StartSmartDesignDialog = ({open, ...props}: StartSmartDesignDialogProps) => {
  const taopix = useTaopix();
  const settings = useSettings();
  const pageCount = useSelector(TotalPageCountSelector);
  const ui = useUiState();
  const {t} = useTranslation(['assistants', 'dialogs']);
  const experience = useExperience();
  const aiMode = experience.getAIMode();
  const isXSmallScreen = MatchesMediaQuery(['(max-height: 600px)']);

  const currency = useMemo(
    () => CurrencyAdapter(settings.state.currency),
    [settings.state.currency.code]
  );

  const getAIPrice = (): JSX.Element | null => {
    if (AIMode.On === aiMode) return null;

    const productQuantity = settings.state.price.pricing.quantity || 0;

    // Create a pricing engine object based on the parameters passed in.
    const pricingEngine = new (taopix.core.get('pricingEngine'))(
      currency,
      productQuantity,
      settings.state.price.pricing,
      true
    );

    const price = pricingEngine.getAIDisplayPrice(pageCount);

    if ('' !== price) {
      return <span className={'text-md'}> (+ {price}*)</span>;
    }

    return null;
  };

  const sectionClassList = classNames('flex flex-col w-full rounded-[10rem] p-lg box-border');
  const ulClassList = classNames(
    'space-y-xs',
    'sm:max-w-[350px]',
    isXSmallScreen ? ['text-sm', 'leading-none', 'my-sm'] : ['my-lg']
  );
  const liClassList = classNames('flex', 'items-start');
  const bulletTextClasses = classNames('flex', 'items-center', 'leading-snug', 'mt-[2px]');
  const responsiveCheckIcon = (
    <Icon icon={<CheckSmallIcon />} size={isXSmallScreen ? 'small' : 'medium'} />
  );
  const aiPrice = getAIPrice();

  return (
    <Dialog
      open={open}
      width={ui.state.isSmallScreen ? 400 : 640}
      theme={'tpx-theme-dialogs'}
      themeTip="Dialogs"
      title={t('dialogs:str_TitleStartSmartDesign', {defaultValue: 'Start Smart Design'})}
      showTitle={false}
    >
      <DialogContent>
        <Theme name={ThemeName.Prominent} className={sectionClassList}>
          <Heading size={isXSmallScreen ? 3 : 2} className={'mb-sm'}>
            {t('str_TitleTrySmartDesignAssistant')}
            {aiPrice}
          </Heading>
          <div className={'flex sm:flex-col lg:space-x-sm sm:justify-center'}>
            <img
              className="self-center"
              src={require('../../../../Images/smart-design.svg').default}
              width="230"
              height="169"
              alt={'An illustration of a photobook containing pictures and a magic wand.'}
            />
            <ul className={ulClassList}>
              <li className={liClassList}>
                {responsiveCheckIcon}
                <p className={bulletTextClasses}>{t('str_MessageAutomaticallyDesignsPages')}</p>
              </li>
              <li className={liClassList}>
                {responsiveCheckIcon}
                <p className={bulletTextClasses}>{t('str_MessageIntelligentPictureSelection')}</p>
              </li>
              <li className={liClassList}>
                {responsiveCheckIcon}
                <p className={bulletTextClasses}>{t('str_MessageFindsBestCrop')}</p>
              </li>
            </ul>
          </div>
        </Theme>
        {aiPrice && (
          <p className={'text-sm'}>* {t('*:str_MessageSmartDesignComponentPriceDisclaimer')}</p>
        )}
      </DialogContent>
      <DialogFooter isSmallScreen={ui.state.isSmallScreen}>
        <Button
          label={t('*:str_ButtonClose')}
          hideLabel
          buttonStyle={'tertiary'}
          onClick={() => props.onClose()}
          size={isXSmallScreen ? 'small' : 'medium'}
        >
          {t('*:str_ButtonClose')}
        </Button>
        <Button
          label={t('str_LabelStartSmartDesign')}
          buttonStyle="primary"
          onClick={() => {
            props.onClose();
            props.onStartSmartDesign();
          }}
          size={isXSmallScreen ? 'small' : 'medium'}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default StartSmartDesignDialog;
